import {
  Card,
  CardHeader,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from "@material-ui/core";
import PropTypes from "prop-types";
import { fNumber, fPercent } from "src/utils/formatNumber";
import { capitalizarPrimeraLetra } from "src/utils/sharedFunctions";
import { useMemo } from "react";

AppDistribucionPropuestas.propTypes = {
  title: PropTypes.string,
  subheader: PropTypes.string,
  data: PropTypes.object,
  cargandoData: PropTypes.bool,
  loadingComponent: PropTypes.bool,
  onClick: PropTypes.func,
};

export default function AppDistribucionPropuestas({
  title,
  subheader,
  data,
  cargandoData,
  loadingComponent,
  onClick,
  ...other
}) {
  const dataGraph = useMemo(() => {
    if (!cargandoData && data) {
      // Combinar datos de todas las sucursales
      const estadosAgrupados = {};

      Object.keys(data).forEach((sucursal) => {
        if (data[sucursal]?.items) {
          data[sucursal].items.forEach((item) => {
            const estado = item.estado;
            if (!estadosAgrupados[estado]) {
              estadosAgrupados[estado] = {
                label: estado,
                value: 0,
                percent: 0,
              };
            }
            estadosAgrupados[estado].value += item.cantidad;
          });
        }
      });

      // Calcular porcentajes totales
      const totalItems = Object.values(estadosAgrupados).reduce(
        (sum, item) => sum + item.value,
        0,
      );

      return Object.values(estadosAgrupados).map((item) => ({
        label: item.label,
        value: item.value,
        percent: totalItems > 0 ? (item.value / totalItems) * 100 : 0,
      }));
    }
    return [];
  }, [cargandoData, data]);

  if (loadingComponent) {
    return (
      <Card {...other}>
        <CardHeader
          title={<Skeleton width="60%" />}
          subheader={<Skeleton width="40%" />}
        />
        <Stack spacing={4} sx={{ p: 3 }}>
          {[1, 2, 3, 4].map((item) => (
            <Stack key={item} spacing={1}>
              <Stack direction="row" alignItems="center">
                <Skeleton width="60%" height={24} />
                <Skeleton width="20%" height={24} />
                <Skeleton width="20%" height={24} />
              </Stack>
              <Skeleton variant="rectangular" height={10} />
            </Stack>
          ))}
        </Stack>
      </Card>
    );
  }

  return (
    <Card {...other}>
      <CardHeader title={title} subheader={subheader} />

      <Stack spacing={4} sx={{ p: 3 }}>
        {dataGraph.map((progress) => (
          <ProgressItem
            key={progress.label}
            progress={progress}
            onClick={onClick}
          />
        ))}
      </Stack>
    </Card>
  );
}

// ----------------------------------------------------------------------

ProgressItem.propTypes = {
  progress: PropTypes.shape({
    percent: PropTypes.number,
    label: PropTypes.string,
    value: PropTypes.number,
    color: PropTypes.string,
  }),
  onClick: PropTypes.func,
};

function ProgressItem({ progress, onClick }) {
  const handleClick = () => {
    if (onClick) {
      onClick(progress.label);
    }
  };

  // Función para determinar el color basado en el estado
  const getColorByState = (estado) => {
    switch (estado?.toUpperCase()) {
      case "ACEPTADA":
        return "success";
      case "RECHAZADA":
        return "error";
      case "ENVIADA":
        return "info";
      case "CREADA":
        return "warning";
      default:
        return "primary";
    }
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body2" sx={{ minWidth: 200 }}>
          {capitalizarPrimeraLetra(progress.label)}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">{fNumber(progress.value)}</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            ({fPercent(progress.percent)})
          </Typography>
        </Stack>
      </Stack>

      <LinearProgress
        variant="determinate"
        value={progress.percent}
        color={getColorByState(progress.label)}
        sx={{
          cursor: onClick ? "pointer" : "default",
          height: 6,
          borderRadius: 4,
        }}
        onClick={handleClick}
      />
    </Stack>
  );
}
